<template>
  <v-row no-gutters class="mt-4">
    <v-col cols="4" class="pr-2">
      <h5 class="section-title pb-1">{{ $t("components.bookingForm.section.flightNumber") }}</h5>
      <v-text-field
        v-model="flightNumberLocal"
        id="flightNumber"
        class="mt-0"
        hide-details
        outlined
        dense
        filled
        height="44px"
        :class="[$v.flightNumberLocal.$invalid ? 'error-border' : '']"
        :placeholder="$t('components.bookingForm.field.flightNumber')"
        @change="onFlightNumberChange"
      >
        <template v-slot:append>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-fade-transition>
                <v-icon
                  v-if="$v.flightNumberLocal.$invalid"
                  class="validation-icon mt-1"
                  v-bind="attrs"
                  v-on="on"
                  small
                >
                  mdi-alert-circle
                </v-icon>
              </v-fade-transition>
            </template>
            <div class="d-flex flex-column">
              <span v-if="!$v.flightNumberLocal.minLength">
                {{
                  $t("components.bookingForm.validation.flightNumber.minLength", {
                    attribute: 3,
                  })
                }}
              </span>
            </div>
          </v-tooltip>
        </template>
      </v-text-field>
    </v-col>

    <v-col cols="4" class="pl-0">
      <h5 class="section-title pb-1" v-if="typeAirportDetails === 'pickup'">{{ $t("components.bookingForm.section.arrivalTime") }}</h5>
      <h5 class="section-title pb-1" v-if="typeAirportDetails === 'dropoff'">{{ $t("components.bookingForm.section.departureTime") }}</h5>
      <validated-field v-slot="{ attrs }" v-model="timeMask" :validator="$v.timeMask">
        <v-text-field
          outlined
          filled
          dense
          height="44px"
          v-bind="attrs"
          v-model="timeMask"
          v-maska
          hide-details
          data-maska="['##:##','@@@@']"
          @focus="handleTimeFocus"
          @input="onTimeChange"
        />
      </validated-field>
      <div class="d-flex flex-column">
        <span v-if="!$v.timeMask.invalidTime" class="error--text text-caption">
          {{ $t("components.bookingForm.section.valid_time") }}
        </span>
        <span v-if="!$v.timeMask.cannotBeInPast" class="error--text text-caption">
          {{ $t("components.bookingForm.section.time_error") }}
        </span>
      </div>
    </v-col>

    <v-col cols="4" class="pl-2">
      <h5 class="section-title pb-1">{{ $t("components.bookingForm.section.terminal") }}</h5>
      <validated-field v-slot="{ attrs }" v-model="terminal" :validator="$v.terminal">
        <v-text-field
          v-model="terminal"
          id="terminal"
          class="mt-0 address-select"
          hide-details
          outlined
          dense
          height="44px"
          filled
          v-bind="attrs"
          :class="[$v.terminal.$invalid ? 'error-border' : '']"
          @change="onTerminalChange"
        />
      </validated-field>
      <div class="d-flex flex-column">
        <span v-if="!$v.terminal.minLength" class="error--text text-caption">
          {{ $t("components.bookingForm.validation.terminal.minLength", {
            attribute: 3,
          }) }}
        </span>
      </div>
    </v-col>

    <v-col cols="4" class="pr-2 mt-1">
      <h5 class="section-title pb-1">{{ $t("components.bookingForm.section.passengers") }}</h5>
      <validated-field v-slot="{ attrs }" v-model="passengers" :validator="$v.passengers">
        <v-text-field
        v-model="passengers"
        id="passengers"
        class="mt-0 address-select"
        hide-details
        outlined
        dense
        height="44px"
        filled
        type="number"
        v-bind="attrs"
        :class="[$v.passengers.$invalid ? 'error-border' : '']"
        @change="onPassengersChange"
        min="1"
      />
      </validated-field>
      <div class="d-flex flex-column">
        <span v-if="!$v.passengers.required" class="error--text text-caption">
          {{ $t("components.bookingForm.validation.passengers") }}
        </span>
      </div>
    </v-col>

    <v-col cols="4" class="pl-0 mt-1">
      <h5 class="section-title pb-1">{{ $t("components.bookingForm.section.smallBags") }}</h5>
      <validated-field v-slot="{ attrs }" v-model="smallBags" :validator="$v.smallBags">
        <v-text-field
        v-model="smallBags"
        id="smallBags"
        class="mt-0 address-select"
        hide-details
        outlined
        dense
        height="44px"
        filled
        type="number"
        v-bind="attrs"
        :class="[$v.smallBags.$invalid ? 'error-border' : '']"
        @change="onSmallBagsChange"
        min="0"
        />
      </validated-field>
    </v-col>

    <v-col cols="4" class="pl-2 mt-1">
      <h5 class="section-title pb-1">{{ $t("components.bookingForm.section.largeBags") }}</h5>
      <validated-field v-slot="{ attrs }" v-model="largeBags" :validator="$v.largeBags">
        <v-text-field
        v-model="largeBags"
        id="largeBags"
        class="mt-0 address-select"
        hide-details
        outlined
        dense
        height="44px"
        filled
        type="number"
        v-bind="attrs"
        :class="[$v.largeBags.$invalid ? 'error-border' : '']"
        @change="onLargeBagsChange"
        min="0"
      />
      </validated-field>
    </v-col>
  </v-row>
</template>

<script>
import moment from "moment-timezone";
import { vMaska, Mask } from "maska";
import { minLength, required } from "vuelidate/lib/validators";

const DATE_FORMAT_YMD = "YYYY-MM-DD";

export default {
  name: "AirportDetails",
  directives: { maska: vMaska },
  props: {
    airportDetails: {
      type: Object,
      required: true,
    },
    typeAirportDetails: {
      type: String,
      default: 'pickup',
    },
  },
  data() {
    return {
      flightNumberLocal: "",
      time: moment().format("HH:mm"),
      timeMask: moment().format("HH:mm"),
      terminal: null,
      passengers: "1",
      largeBags: "0",
      smallBags: "0",
    };
  },
  mounted() {
    const time = this.airportDetails.arrivalTime ? moment.tz(this.airportDetails.arrivalTime, "UTC").format("HH:mm") : moment().format("HH:mm");
    this.flightNumberLocal = this.airportDetails.number;
    this.time = time;
    this.timeMask = time;
    this.terminal = this.airportDetails.terminal;
    this.passengers = this.airportDetails.totalPassengers.toString();
    this.smallBags = this.airportDetails.smallBags.toString();
    this.largeBags = this.airportDetails.largeBags.toString();
  },
  watch: {
    flightNumberLocal() {
      this.emitAirportDetails();
    },
    time() {
      this.emitAirportDetails();
    },
    terminal() {
      this.emitAirportDetails();
    },
    passengers() {
      this.emitAirportDetails();
    },
    smallBags() {
      this.emitAirportDetails();
    },
    largeBags() {
      this.emitAirportDetails();
    },
  },
  validations() {
    const validation = {
      flightNumberLocal: {
        minLength: minLength(3),
      },
      timeMask: {
        invalidTime: (val) => {
          return moment(val, "HH:mm", true).isValid();
        },
        cannotBeInPast: (val) => {
          if (this.date !== moment().format(DATE_FORMAT_YMD)) return true;
          if (!val || !moment(val, "HH:mm", true).isValid()) return true;
          return moment(val, "HH:mm", true).isSameOrAfter(moment(), "minute");
        },
      },
      passengers: {
        required,
        minValue: (val) => Number(val) >= 1,
      },
      smallBags: {
        minValue: (val) => Number(val) >= 0,
      },
      largeBags: {
        minValue: (val) => Number(val) >= 0,
      },
      terminal: {
        minLength: minLength(1),
      },
    };

    return validation;
  },
  methods: {
    emitAirportDetails() {
      const currentDate = moment().format("YYYY-MM-DD");
      const currentTime = this.time;

      this.$emit('airportDetailsChanged', {
        flightNumber: this.flightNumberLocal,
        time: moment.tz(`${currentDate} ${currentTime}`, "YYYY-MM-DD HH:mm", "UTC"),
        terminal: this.terminal,
        passengers: Number(this.passengers) || 1,
        smallBags: Number(this.smallBags) || 0,
        largeBags: Number(this.largeBags) || 0,
        isValid: !this.$v.$invalid,
        type: this.typeAirportDetails,
      });
    },
    onFlightNumberChange() {
      this.$emit('onFlightNumberChange', this.flightNumberLocal);
      this.emitAirportDetails();
    },
    onTerminalChange() {
      this.$emit('onTerminalChange', this.terminal);
      this.emitAirportDetails();
    },
    onPassengersChange() {
      if (this.passengers < 1) {
        this.passengers = 1;
      }
      this.$emit('onPassengersChange', this.passengers);
      this.emitAirportDetails();
    },
    onSmallBagsChange() {
      if (this.smallBags < 0) {
        this.smallBags = 0;
      }
      this.$emit('onSmallBagsChange', this.smallBags);
      this.emitAirportDetails();
    },
    onLargeBagsChange() {
      if (this.largeBags < 0) {
        this.largeBags = 0;
      }
      this.$emit('onLargeBagsChange', this.largeBags);
      this.emitAirportDetails();
    },
    maskInput(ele) {
      const val = ele.target.value.toUpperCase().trim();
      if (!val) {
        this.time = moment().format("HH:mm");
        this.timeMask = moment().format("HH:mm");
        return;
      }

      const mask = new Mask({ mask: "##:##" });
      if (val.length >= 4) {
        this.time = mask.masked(val);
        this.timeMask = mask.masked(val);
      } else if (val.length === 1) {
        this.time = mask.masked(`0${val}:00`);
        this.timeMask = mask.masked(`0${val}:00`);
      } else if (val.length === 2) {
        this.time = mask.masked(`${val}:00`);
        this.timeMask = mask.masked(`${val}:00`);
      }

      this.$emit('pickupTimeChanged', {
        isAsap: false,
        date: this.date,
        time: this.time,
        isTimeValid: !this.$v.timeMask.$invalid,
      });
    },
    onTimeChange(val) {
      this.time = moment(val, "HH:mm").format("HH:mm");
      this.emitAirportDetails();
    },
    handleTimeFocus(ele) {
      ele.target.select();
    },
    handleTimeBlurEvent(ele) {
      if (ele.target.value.toUpperCase() === '') {
        this.timeMask = moment().format("HH:mm");
        this.time = moment().format("HH:mm");
      }
      this.maskInput(ele);
    },
  }
};
</script>

<style lang="scss">
.flightNumberField {
  font-family: "Inter";
  font-style: normal;
  font-weight: 600 !important;
  font-size: 12px !important;
  line-height: 15px;
  display: flex;
  align-items: center;
  letter-spacing: -0.005em !important;
  color: #333333 !important;
  background-color: #f0f0f0 !important;

  & fieldset {
    border: 0px !important;
  }
}

.validation-icon {
  color: #ff0000 !important;
}
</style>
